/**
 * @summary - Component that Provides Asset Catgeory Creation Page
 * @author Yuvasri S
 * @since 2024-05-28
 */
import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Grid } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { BreadcrumbBar } from "../../assets/styled-components/breadcrumbBar";
import CustomTextField from "../commoncomponents/CustomTextField";
import { DefaultCard } from "../../assets/styled-components/card";
import CustomFooter from "../commoncomponents/CustomFooter";
import { useNavigate, useLocation } from "react-router";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import axios from "axios";
import {
  getAuthToken,
  getcompanyId,
  getFacilityId,
  getCreatedBy,
  getModifiedBy,
} from "../../utility/tokenUtility";
import { MasterGraph } from "../../utility/apiURLS";
import CustomDialog from "../commoncomponents/CustomDialog";
import HomeIconButton from "../commoncomponents/HomeIconButton";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import CustomTextArea from "../commoncomponents/CustomTextArea";

const CreateAssetCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [service, setService] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [assetcategory, setAssetCategoryCode] = useState("");
  const [assetcategorydesc, setAssetCategoryDesc] = useState("");
  const [assetcategoryId, setAssetCategoryId] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyEdit, setReadOnlyEdit] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  //cancel validation
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  //Addnew validation
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [unsavedAddDialogOpen, setUnsavedAddDialogOpen] = useState(false);

  const [errors, setErrors] = useState({
    service: false,
    assetcategory: false,
    assetcategorydesc: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    service: "",
    assetcategory: "",
    assetcategorydesc: "",
  });

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");
  const facilityId = getFacilityId("facilityId");
  const createdBy = getCreatedBy("createdBy");
  const modifiedBy = getModifiedBy("modifiedBy");

  useEffect(() => {
    const fetchServiceOptions = async () => {
      try {
        const response = await axios.get(
          `${MasterGraph}/api/masters/GetMasters/${companyId}/${facilityId}`,
          config
        );
        const serviceData = response.data.masservicedetails;
        const options = serviceData.map((service) => ({
          value: service.servicedesc,
          label: service.service,
          id: service.id,
        }));
        setServiceOptions(options);

        // Check if there is state passed from the navigation
        if (location.state) {
          const {
            servicename,
            assetcategory,
            assetcategorydesc,
            id,
            readOnly,
            readOnlyEdit,
            mode,
          } = location.state;

          const selectedService = options.find(
            (option) => option.label === servicename
          );

          setService(selectedService);
          setAssetCategoryCode(assetcategory);
          setAssetCategoryDesc(assetcategorydesc);
          setAssetCategoryId(id);
          setReadOnly(!!readOnly);
          setReadOnlyEdit(!!readOnlyEdit);
          if (mode === "edit") {
            setIsEdit(true);
            setIsView(false);
          } else if (mode === "view") {
            setIsView(true);
            setIsEdit(false);
          } else {
            setIsEdit(false);
            setIsView(false);
          }
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServiceOptions();
  }, [location.state]);

  const handleSubmit = (e) => {
    e.preventDefault();

    let formIsValid = true;
    const newErrors = {
      service: false,
      assetcategory: false,
      assetcategorydesc: false,
    };
    const newErrorMessages = {
      service: "",
      assetcategory: "",
      assetcategorydesc: "",
    };

    if (!service) {
      formIsValid = false;
      newErrors.service = true;
      newErrorMessages.service = "Service is required";
    }
    if (!assetcategory) {
      formIsValid = false;
      newErrors.assetcategory = true;
      newErrorMessages.assetcategory = "Asset Category Code is required";
    } else if (assetcategory.startsWith(" ")) {
      formIsValid = false;
      newErrors.assetcategory = true;
      newErrorMessages.assetcategory = "Space not allowed at the beginning";
    } else if (assetcategory.includes("  ")) {
      formIsValid = false;
      newErrors.assetcategory = true;
      newErrorMessages.assetcategory = "Double Space not allowed";
    } else if (!/^[a-zA-Z0-9 /-]*$/.test(assetcategory)) {
      formIsValid = false;
      newErrors.assetcategory = true;
      newErrorMessages.assetcategory =
        "Invalid Characters in Asset Category Code";
    }

    if (!assetcategorydesc) {
      formIsValid = false;
      newErrors.assetcategorydesc = true;
      newErrorMessages.assetcategorydesc =
        "Asset Category Description is required";
    } else if (assetcategorydesc.startsWith(" ")) {
      formIsValid = false;
      newErrors.assetcategorydesc = true;
      newErrorMessages.assetcategorydesc = "Space not allowed at the beginning";
    } else if (assetcategorydesc.includes("  ")) {
      formIsValid = false;
      newErrors.assetcategorydesc = true;
      newErrorMessages.assetcategorydesc = "Double Space not allowed";
    } else if (!/^[a-zA-Z0-9,!@#$%&*()_\- .]*$/.test(assetcategorydesc)) {
      formIsValid = false;
      newErrors.assetcategorydesc = true;
      newErrorMessages.assetcategorydesc =
        "Invalid Characters in Asset Category Description";
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);

    if (formIsValid) {
      handleSave();
      setHasUnsavedChanges(false);
    }
  };

  const handleCancel = () => {
    if (isView) {
      navigate(-1, { replace: true });
    } else {
      if (hasUnsavedChanges) {
        setCancelDialogOpen(true);
      } else {
        navigate(-1, { replace: true });
      }
    }
  };


  const handleServiceChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setService(newValue);
    if (newValue) {
      setErrors((prevErrors) => ({ ...prevErrors, service: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, service: "" }));
    }
  };
  const handleAcCodeChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setAssetCategoryCode(newValue);
    const regex = /^[a-zA-Z0-9 /-]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategory: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategory: "Invalid Characters in Asset Category Code",
      }));
    } else if (newValue.startsWith(" ")) {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategory: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategory: "Space not allowed at the beginning",
      }));
    } else if (newValue.includes("  ")) {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategory: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategory: "Double Space not allowed",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategory: false }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategory: "",
      }));
    }
  };

  const handleAcDescChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setAssetCategoryDesc(newValue);
    const regex = /^[a-zA-Z0-9,!@#$%&*()_\- .]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategorydesc: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategorydesc: "Invalid Characters in Asset Category Description",
      }));
    } else if (newValue.startsWith(" ")) {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategorydesc: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategorydesc: "Space not allowed at the beginning",
      }));
    } else if (newValue.includes("  ")) {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategorydesc: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategorydesc: "Double Space not allowed",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, assetcategorydesc: false }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        assetcategorydesc: "",
      }));
    }
  };

  const handleSave = async () => {
    try {
      if (isEdit) {
        const payload = {
          companyid: companyId,
          assetcategory,
          assetcategorydesc,
          id: assetcategoryId,
          masservicedetailsId: service.id,
          // masservicedetails: {
          //   service: service.label,
          //   servicedesc: service.value,
          //   id: service.id,
          // },
          modifiedby: modifiedBy,
        };

        await axios.put(
          `${MasterGraph}/api/masassetcategories/${assetcategoryId}`,
          payload,
          config
        );
      } else {
        const payload = {
          companyid: companyId,
          assetcategory,
          assetcategorydesc,
          id: assetcategoryId,
          masservicedetails: {
            service: service.label,
            servicedesc: service.value,
            id: service.id,
          },
          createdby: createdBy,
        };

        await axios.post(
          `${MasterGraph}/api/masassetcategories`,
          payload,
          config
        );
      }

      setIsSubmitted(true);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error saving asset category:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage("Asset Category Code Already Exists");
      } else {
        setErrorMessage(
          "An unexpected error occurred. Please try again later."
        );
      }
      setErrorDialogOpen(true);
    }
  };
  const handleAdd = () => {
    if (!isSubmitted && (service || assetcategory || assetcategorydesc)) {
      setUnsavedAddDialogOpen(true);
    } else {
      clearForm();
    }
  };

  const clearForm = () => {
    setService("");
    setAssetCategoryCode("");
    setAssetCategoryDesc("");
    setIsEdit(false);
    setIsView(false);
    setAssetCategoryId("");
    setErrors({
      service: false,
      assetcategory: false,
      assetcategorydesc: false,
    });
    setErrorMessages({ service: "", assetcategory: "", assetcategorydesc: "" });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    // if (isEdit) {
    //   navigate(-1, { replace: true });
    // }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };
  const handleCancelDialogConfirm = () => {
    setCancelDialogOpen(false);
    navigate(-1, { replace: true });
  };
  const handleUnsavedAddDialogClose = () => {
    setUnsavedAddDialogOpen(false);
  };

  const handleUnsavedAddConfirm = () => {
    clearForm();
    setUnsavedAddDialogOpen(false);
  };

  return (
    <>
      <PageTitle
        title={"Create Asset Category"}
        isEdit={isEdit}
        isView={isView}
        editTitle={"Edit Asset Category"}
        viewTitle={"View Asset Category"}
      />

      <DefaultCard sx={{ height: "100%" }}>
        <form onSubmit={handleSubmit} style={{ position: "relative" }}>
          <Box mb={2}>
            <Grid container gap={3}>
              <Grid item xs={6} md={3.8}>
                <CustomSelectInput
                  id="service-label"
                  label="Service"
                  value={service}
                  options={serviceOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={handleServiceChange}
                  disabled={readOnly || readOnlyEdit}
                  isMandatoryField={true}
                  error={errors.service}
                  errorMessage={errorMessages.service}
                  autoFocus
                />
              </Grid>
              <Grid item xs={6} md={3.8}>
                <CustomTextField
                  maxlength={6}
                  label="Asset Category Code"
                  id="asset-category"
                  value={assetcategory}
                  onChange={handleAcCodeChange}
                  disabled={readOnly || readOnlyEdit}
                  isMandatoryField={true}
                  error={errors.assetcategory}
                  errorMessage={errorMessages.assetcategory}
                />
              </Grid>
              <Grid item xs={6} md={3.8}>
                {/* <CustomTextField
                  maxlength={255}
                  label="Asset Category Description"
                  id="asset-category-description"
                  value={assetcategorydesc}
                  onChange={handleAcDescChange}
                  disabled={readOnly}
                  isMandatoryField={true}
                  error={errors.assetcategorydesc}
                  errorMessage={errorMessages.assetcategorydesc}
                /> */}
                <CustomTextArea
                  maxLength={255}
                  label="Asset Category Description"
                  id="asset-category-description"
                  value={assetcategorydesc}
                  onChange={handleAcDescChange}
                  disabled={readOnly}
                  isMandatoryField={true}
                  error={errors.assetcategorydesc}
                  helperText={errorMessages.assetcategorydesc}
                />
              </Grid>
            </Grid>
          </Box>
        </form>
        <CustomFooter
          cancelClick={handleCancel}
          saveClick={handleSubmit}
          addClick={handleAdd}
          showAddButton={true}
          readOnly={readOnly}
          buttonLabel={isEdit ? "Update" : "Save"}
          isEdit={isEdit}
        />
      </DefaultCard>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: { xs: "center", md: "end" },
        }}
      ></Box>

      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogClose}
        title="Success"
        confirmBtn="Ok"
        content={
          isEdit ? (
            <>Data updated successfully.</>
          ) : (
            <>Data saved successfully.</>
          )
        }
      />
      <CustomDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        confirmBtn="Yes"
        cancelBtn="No"
        onConfirm={handleCancelDialogConfirm}
        onCancel={handleCancelDialogClose}
      />
      <CustomDialog
        open={unsavedAddDialogOpen}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        onConfirm={handleUnsavedAddConfirm}
        onClose={handleUnsavedAddDialogClose}
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        onConfirm={() => setErrorDialogOpen(false)}
        title="Error"
        confirmBtn="Ok"
        content={errorMessage}
      />
    </>
  );
};

export default CreateAssetCategory;
